import React, { useMemo } from 'react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { QueryResponse, DeptSalarySummary, SalaryCategory } from '../../../services/pw933aApi';

interface ResultTableProps {
  result: QueryResponse;
}

const formatAmount = (v: number): string =>
  v.toLocaleString('zh-TW', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const ResultTable: React.FC<ResultTableProps> = ({ result }) => {
  // Collect all unique salary categories across all departments
  const allCategories = useMemo(() => {
    const catMap = new Map<string, string>();
    for (const dept of result.data) {
      for (const cat of dept.categories) {
        if (!catMap.has(cat.code)) {
          catMap.set(cat.code, cat.name);
        }
      }
    }
    // Also include grand total categories
    for (const cat of result.grandTotal.categories) {
      if (!catMap.has(cat.code)) {
        catMap.set(cat.code, cat.name);
      }
    }
    return Array.from(catMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([code, name]) => ({ code, name }));
  }, [result]);

  // Build flat row data with category amounts as columns
  type RowData = {
    key: string;
    deptCode: string;
    deptName: string;
    empCount: number;
    total: number;
    isGrandTotal?: boolean;
    [catKey: string]: string | number | boolean | undefined;
  };

  const dataSource: RowData[] = useMemo(() => {
    const rows: RowData[] = result.data.map((dept: DeptSalarySummary) => {
      const row: RowData = {
        key: dept.deptCode,
        deptCode: dept.deptCode,
        deptName: dept.deptName,
        empCount: dept.empCount,
        total: dept.total,
      };
      for (const cat of dept.categories) {
        row[`cat_${cat.code}`] = cat.amount;
      }
      return row;
    });

    // Grand total row
    const grandRow: RowData = {
      key: '__grand_total__',
      deptCode: '',
      deptName: '合計',
      empCount: result.grandTotal.empCount,
      total: result.grandTotal.total,
      isGrandTotal: true,
    };
    for (const cat of result.grandTotal.categories) {
      grandRow[`cat_${cat.code}`] = cat.amount;
    }
    rows.push(grandRow);

    return rows;
  }, [result]);

  const columns: ColumnsType<RowData> = useMemo(() => {
    const cols: ColumnsType<RowData> = [
      {
        title: '部門代碼',
        dataIndex: 'deptCode',
        key: 'deptCode',
        width: 100,
        fixed: 'left',
        render: (v: string, rec: RowData) =>
          rec.isGrandTotal ? <strong>{rec.deptName}</strong> : v,
      },
      {
        title: '部門名稱',
        dataIndex: 'deptName',
        key: 'deptName',
        width: 120,
        fixed: 'left',
        render: (v: string, rec: RowData) =>
          rec.isGrandTotal ? '' : v,
      },
      {
        title: '人數',
        dataIndex: 'empCount',
        key: 'empCount',
        width: 70,
        align: 'right',
        render: (v: number, rec: RowData) =>
          rec.isGrandTotal ? <strong>{v}</strong> : v,
      },
    ];

    // Add one column per salary category
    for (const cat of allCategories) {
      cols.push({
        title: cat.name || cat.code,
        dataIndex: `cat_${cat.code}`,
        key: `cat_${cat.code}`,
        width: 110,
        align: 'right',
        render: (v: number | undefined, rec: RowData) => {
          const amt = v ?? 0;
          const formatted = formatAmount(amt);
          return rec.isGrandTotal ? <strong>{formatted}</strong> : formatted;
        },
      });
    }

    cols.push({
      title: '合計',
      dataIndex: 'total',
      key: 'total',
      width: 120,
      align: 'right',
      fixed: 'right',
      render: (v: number, rec: RowData) => {
        const formatted = formatAmount(v);
        return rec.isGrandTotal ? <strong>{formatted}</strong> : formatted;
      },
    });

    return cols;
  }, [allCategories]);

  return (
    <div>
      <div className="mb-2 text-sm text-gray-500">
        共 {result.data.length} 個部門，{result.grandTotal.empCount} 位員工
      </div>
      <Table<RowData>
        dataSource={dataSource}
        columns={columns}
        rowKey="key"
        size="small"
        pagination={false}
        bordered
        scroll={{ x: 'max-content' }}
        rowClassName={(rec) => (rec.isGrandTotal ? 'bg-blue-50 font-semibold' : '')}
      />
    </div>
  );
};

export default ResultTable;
